import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Backdrop,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    InputAdornment,
    List,
    ListItemButton,
    ListItemText,
    TextField,
    Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { Customer, Order, Product } from "../../../types";
import { orderService } from "../../../services/orderService";
import { productService } from "../../../services/productService";
import { OrderDetailDrawer } from "../../../components/orders/OrderDetailDrawer";
import { useOrderActions } from "../../../components/orders/useOrderActions";
import { useCustomerDetails } from "../../../components/customers/useCustomerDetails";
import { ProductDetailDrawer } from "../../../components/products/ProductDetailDrawer";
import { CustomerSearchAutocomplete } from "../../../components/common/CustomerSearchAutocomplete";
import { ProductSearchAutocomplete } from "../../../components/common/ProductSearchAutocomplete";
import { LinkKind } from "./TaskBits";

/**
 * Opens a task's linked order, customer or product in the same drawer or
 * dialog the rest of the admin uses. Render `element` once; call `open`.
 */
export function useRecordViewer(onError?: (message: string) => void) {
    const [order, setOrder] = useState<Order | null>(null);
    const [product, setProduct] = useState<Product | null>(null);
    const [busy, setBusy] = useState(false);
    const orderActions = useOrderActions({ onOrderUpdated: setOrder, onError });
    const { showCustomer, loadingCustomerId, dialog: customerDialog } = useCustomerDetails();

    const open = useCallback(
        async (kind: LinkKind, id: string) => {
            if (kind === "customer") {
                showCustomer(id);
                return;
            }
            setBusy(true);
            try {
                if (kind === "order") setOrder(await orderService.getOrderById(id));
                else setProduct(await productService.getProductById(id));
            } catch {
                onError?.(`Couldn't open that ${kind}.`);
            } finally {
                setBusy(false);
            }
        },
        [showCustomer, onError],
    );

    const element = (
        <>
            <OrderDetailDrawer
                open={!!order}
                onClose={() => setOrder(null)}
                order={order}
                {...orderActions.drawerActionProps}
                onPaymentAdded={setOrder}
            />
            {orderActions.dialogs}
            {customerDialog}
            <ProductDetailDrawer open={!!product} onClose={() => setProduct(null)} product={product} onProductChanged={setProduct} />
            <Backdrop open={busy || !!loadingCustomerId || orderActions.busy} sx={{ zIndex: (t) => t.zIndex.modal + 2, color: "#fff" }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    );

    return { open, element };
}

// ── Picking a record to link ────────────────────────────────────────────────

export interface PickedLink {
    kind: LinkKind;
    id: string;
    label: string;
}

interface LinkPickerProps {
    kind: LinkKind | null;
    onClose: () => void;
    onPick: (link: PickedLink) => void;
}

const TITLES: Record<LinkKind, string> = { order: "Link an order", customer: "Link a customer", product: "Link a product" };

export const LinkPickerDialog: React.FC<LinkPickerProps> = ({ kind, onClose, onPick }) => {
    const pick = (link: PickedLink) => {
        onPick(link);
        onClose();
    };
    return (
        <Dialog open={!!kind} onClose={onClose} fullWidth maxWidth="sm">
            {kind && <DialogTitle sx={{ pb: 1 }}>{TITLES[kind]}</DialogTitle>}
            <DialogContent sx={{ pt: "8px !important", minHeight: 200 }}>
                {kind === "order" && <OrderFinder onPick={(o) => pick({ kind: "order", id: o.order_id, label: o.order_number })} />}
                {kind === "customer" && (
                    <CustomerSearchAutocomplete
                        value={null}
                        label="Search by name or phone"
                        onChange={(c: Customer | null) => {
                            if (c) pick({ kind: "customer", id: c.customer_id, label: [c.first_name, c.last_name].filter(Boolean).join(" ") });
                        }}
                    />
                )}
                {kind === "product" && (
                    <ProductSearchAutocomplete
                        value={null}
                        label="Search products"
                        onChange={(p: Product | null) => {
                            if (p) pick({ kind: "product", id: p.product_id, label: p.name });
                        }}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};

/** Type an order number (or part of it) and pick from the matches. */
const OrderFinder: React.FC<{ onPick: (order: Order) => void }> = ({ onPick }) => {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<Order[]>([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState(0);
    const request = useRef(0);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 3) {
            setResults([]);
            return;
        }
        const id = ++request.current;
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await orderService.searchOrders({ search_query: q, limit: 8, sort_by: "order_date", sort_order: "desc" });
                if (id === request.current) {
                    setResults(res.items);
                    setActive(0);
                }
            } catch {
                if (id === request.current) setResults([]);
            } finally {
                if (id === request.current) setLoading(false);
            }
        }, 250);
        return () => clearTimeout(timer);
    }, [query]);

    return (
        <Box>
            <TextField
                autoFocus
                fullWidth
                placeholder="Order number, customer name or phone"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === "ArrowDown") {
                        e.preventDefault();
                        setActive((i) => Math.min(i + 1, results.length - 1));
                    } else if (e.key === "ArrowUp") {
                        e.preventDefault();
                        setActive((i) => Math.max(i - 1, 0));
                    } else if (e.key === "Enter" && results[active]) {
                        e.preventDefault();
                        onPick(results[active]);
                    }
                }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search />
                            </InputAdornment>
                        ),
                        endAdornment: loading ? <CircularProgress size={18} /> : null,
                    },
                }}
            />
            <List dense sx={{ mt: 1 }}>
                {results.map((o, i) => (
                    <ListItemButton key={o.order_id} selected={i === active} onClick={() => onPick(o)} onMouseEnter={() => setActive(i)}>
                        <ListItemText
                            primary={o.order_number}
                            secondary={[o.customer_name, o.status, o.order_date ? new Date(o.order_date).toLocaleDateString() : null]
                                .filter(Boolean)
                                .join(" · ")}
                        />
                    </ListItemButton>
                ))}
                {query.trim().length >= 3 && !loading && results.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                        No orders match.
                    </Typography>
                )}
            </List>
        </Box>
    );
};
